import { type RadioChangeEvent } from "antd/lib/radio/interface";
import { useEffect, useState } from "react";

import { BTRadio, BTRadioGroup } from "@btnet/core-components";

import { BdsText } from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTUploadRaw } from "commonComponents/btWrappers/BTUploadRaw/BTUploadRaw";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import {
    AUTOFILL_ALLOWED_FILE_TYPES,
    autofillUrlSchema,
} from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

import { AutofillModalShell } from "entity/selectionOption/common/AutofillModalShell/AutofillModalShell";
import { useAutofillDocumentUpload } from "entity/selectionOption/common/useAutofillDocumentUpload";
import { useAutofillUrlField } from "entity/selectionOption/common/useAutofillUrlField";
import { type IAutofillTempFileData as IAutofillOptionTempFileData } from "entity/selectionOptions/SelectionOption/SelectionOption.types";

import "./AutofillOptionModal.less";

type AutofillOptionMethod = "url" | "document";

export type AutofillOptionModalSubmission =
    | { type: "url"; url: string }
    | { type: "document"; tempFile: IAutofillOptionTempFileData };

interface IAutofillOptionModalProps {
    jobId: number;
    visible: boolean;
    /** Pre-fills and pre-selects the URL option when a Product URL has already been entered. */
    initialProductUrl?: string;
    onCancel: () => void;
    onSubmit: (submission: AutofillOptionModalSubmission) => void;
}

const AutofillOptionModalInternal = (props: Readonly<IAutofillOptionModalProps>) => {
    const [selectedMethod, setSelectedMethod] = useState<AutofillOptionMethod>("url");
    const {
        url,
        urlError,
        validateUrl,
        handleUrlChange,
        handleUrlBlur,
        reset: resetUrlField,
    } = useAutofillUrlField();
    const {
        fileList,
        uploadedTempFile,
        handleUploadChange,
        handleUploadRemove,
        reset: resetDocumentUpload,
    } = useAutofillDocumentUpload();

    // resetUrlField/resetDocumentUpload are memoized with stable (empty) deps in their hooks, so
    // including them here is safe — this only re-fires when visible/initialProductUrl change,
    // not on every render.
    useEffect(() => {
        if (!props.visible) return;
        setSelectedMethod("url");
        resetDocumentUpload();

        // Only seed URL if it is valid, otherwise leave blank to avoid showing an error on open.
        const seedUrl = props.initialProductUrl?.trim() ?? "";
        resetUrlField(seedUrl && autofillUrlSchema.isValidSync(seedUrl) ? seedUrl : "");
    }, [props.visible, props.initialProductUrl, resetUrlField, resetDocumentUpload]);

    const handleCancel = () => {
        props.onCancel();
    };

    const isUrlMethod = selectedMethod === "url";
    const submitDisabled = isUrlMethod ? !url.trim() || !!urlError : !uploadedTempFile;

    const handleAutofillOption = async () => {
        if (isUrlMethod) {
            const isValid = await validateUrl(url);
            if (!isValid) return;
            props.onSubmit({ type: "url", url });
        } else {
            if (!uploadedTempFile) return;
            props.onSubmit({ type: "document", tempFile: uploadedTempFile });
        }
    };

    return (
        <AutofillModalShell
            className="AutofillOptionModal"
            testIdBase="autofillOption"
            modalTitle="Use AI to autofill selection option details"
            layoutTitleText="Autofill selection option"
            visible={props.visible}
            onCancel={handleCancel}
            footerContent={
                <BTButton
                    type="primary"
                    data-testid="autofillOptionSubmit"
                    disabled={submitDisabled}
                    onClick={() => void handleAutofillOption()}
                >
                    Autofill
                </BTButton>
            }
        >
            <BTRadioGroup
                className="autofill-option-methodRow padding-top-sm"
                data-testid="autofillOptionMethodSelector"
                value={selectedMethod}
                onChange={(e: RadioChangeEvent) =>
                    setSelectedMethod(e.target.value as AutofillOptionMethod)
                }
            >
                <BTRadio value="url" data-testid="autofillOptionMethodUrl">
                    From a URL
                </BTRadio>
                <BTRadio value="document" data-testid="autofillOptionMethodDocument">
                    From a document
                </BTRadio>
            </BTRadioGroup>
            {isUrlMethod ? (
                <>
                    <BdsText
                        size="distinct-sm"
                        className="text-fg-secondary autofill-option-subtitle"
                    >
                        Use AI to autofill details from a website.
                    </BdsText>
                    <BTFormItem
                        validateStatus={urlError ? "error" : undefined}
                        help={urlError ?? undefined}
                    >
                        <BTInput
                            aria-label="Product URL"
                            id="productUrl"
                            data-testid="productUrlInput"
                            placeholder="Paste or type a URL"
                            size="middle"
                            value={url}
                            onChange={(_field, value) => handleUrlChange(value)}
                            onBlur={() => handleUrlBlur()}
                        />
                    </BTFormItem>
                </>
            ) : (
                <>
                    <BdsText size="distinct-sm" className="text-fg-secondary">
                        Upload a document and we'll turn it into a selection option.
                    </BdsText>
                    <BdsText size="normal-sm" className="text-fg-secondary">
                        Each upload creates one option, pulling in as many line items as your
                        document has. Covers multiple options? Split them into separate files first.
                    </BdsText>
                    <BTUploadRaw
                        id="autofillOptionDocument"
                        data-testid="autofillOptionDocumentUpload"
                        value={fileList}
                        allowedFileTypes={AUTOFILL_ALLOWED_FILE_TYPES}
                        multiple={false}
                        maxCount={1}
                        tempFileType={TempFileTypes.SelectionOption}
                        jobId={props.jobId}
                        isDragger
                        showUploadList
                        onChange={handleUploadChange}
                        onRemove={handleUploadRemove}
                    >
                        <BdsText size="normal-sm" className="text-fg-secondary">
                            <>{AUTOFILL_ALLOWED_FILE_TYPES.map((ft) => `.${ft}`).join(", ")}</>
                        </BdsText>
                    </BTUploadRaw>
                </>
            )}
        </AutofillModalShell>
    );
};

export const AutofillOptionModal = withErrorBoundary(AutofillOptionModalInternal)(
    "Could not load Autofill option modal",
);
